import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getRoleLabel } from '../config/role-routes';
import { clearAllOfflineData, getOfflineStats } from '../db/schema';

export function SettingsPage() {
  const { user, logout } = useAuth();
  const { showSuccess, showInfo, showError } = useToast();
  const [stats, setStats] = useState<{
    reports: number;
    photos: number;
    messages: number;
    changeOrders: number;
    pendingSync: number;
  } | null>(null);
  const [isClearing, setIsClearing] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await getOfflineStats();
        setStats({
          reports: s.reports,
          photos: s.photos,
          messages: s.messages,
          changeOrders: s.changeOrders,
          pendingSync: s.pendingSync,
        });
      } catch (err) {
        console.error(err);
      }
    })();

    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) {
      showInfo('Usa el menú de tu navegador → “Añadir a pantalla de inicio”');
      return;
    }
    installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      showSuccess('App instalada');
    }
    setInstallPrompt(null);
  };

  const handleClearOffline = async () => {
    if (!confirm('¿Eliminar todos los datos offline no sincronizados? Esta acción no se puede deshacer.')) return;
    setIsClearing(true);
    try {
      await clearAllOfflineData();
      showSuccess('Datos offline eliminados');
      setStats({ reports: 0, photos: 0, messages: 0, changeOrders: 0, pendingSync: 0 });
    } catch (err: any) {
      showError(err?.message || 'Error al limpiar');
    } finally {
      setIsClearing(false);
    }
  };

  const handleLogout = async () => {
    if (!confirm('¿Cerrar sesión?')) return;
    await logout();
    window.location.href = '/login';
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <section className="bg-white rounded-xl shadow-sm p-4">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Cuenta</h2>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-lg">
            {(user?.name ?? 'U').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            <p className="text-xs text-primary mt-0.5">{getRoleLabel(user?.role)}</p>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-xl shadow-sm p-4">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Datos offline</h2>
        {stats ? (
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-slate-50 rounded-lg p-2.5">
              <p className="text-xs text-slate-500">Reportes</p>
              <p className="font-bold">{stats.reports}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2.5">
              <p className="text-xs text-slate-500">Fotos</p>
              <p className="font-bold">{stats.photos}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2.5">
              <p className="text-xs text-slate-500">Mensajes</p>
              <p className="font-bold">{stats.messages}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2.5">
              <p className="text-xs text-slate-500">Pendientes sync</p>
              <p className="font-bold text-primary">{stats.pendingSync}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Cargando…</p>
        )}
        <button
          onClick={handleClearOffline}
          disabled={isClearing}
          className="mt-3 w-full py-2 rounded-lg bg-red-50 text-red-600 font-semibold text-sm disabled:opacity-50"
        >
          {isClearing ? 'Limpiando…' : 'Limpiar datos offline'}
        </button>
      </section>

      <section className="bg-white rounded-xl shadow-sm p-4 space-y-2">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Aplicación</h2>
        <button
          onClick={handleInstall}
          className="w-full py-2.5 rounded-lg bg-slate-100 text-slate-800 font-semibold text-sm"
        >
          📲 Instalar en este dispositivo
        </button>
        <button
          onClick={handleLogout}
          className="w-full py-2.5 rounded-lg bg-red-50 text-red-600 font-semibold text-sm"
        >
          Cerrar sesión
        </button>
      </section>

      <p className="text-center text-xs text-slate-400 py-4">
        GranArquiPWA v1.03 · PWA Interna
      </p>
    </div>
  );
}